/**
 * CallHistory — paginated log of past calls fetched from the REST API.
 */

import React, { useCallback, useEffect, useState } from 'react';
import type { CallLog } from '../types';

interface Props {
  userId:       string;
  apiBaseUrl:   string;
}

const STATUS_LABEL: Record<CallLog['status'], string> = {
  initiated: '📲 Initiated',
  ringing:   '🔔 Ringing',
  active:    '📞 Active',
  ended:     '✅ Ended',
  missed:    '📵 Missed',
  rejected:  '❌ Rejected',
};

function formatDuration(secs?: number) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const CallHistory: React.FC<Props> = ({ userId, apiBaseUrl }) => {
  const [calls, setCalls]     = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [offset, setOffset]   = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchCalls = useCallback(
    async (off: number) => {
      if (!userId || !apiBaseUrl) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${apiBaseUrl}/calls/${userId}?limit=${LIMIT}&offset=${off}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCalls((prev) => (off === 0 ? data.calls : [...prev, ...data.calls]));
        setHasMore(data.calls.length === LIMIT);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load call history');
      } finally {
        setLoading(false);
      }
    },
    [userId, apiBaseUrl]
  );

  useEffect(() => {
    fetchCalls(0);
  }, [fetchCalls]);

  const loadMore = () => {
    const next = offset + LIMIT;
    setOffset(next);
    fetchCalls(next);
  };

  return (
    <div className="call-history">
      <div className="call-history__header">
        <h2>Call History</h2>
        <button className="btn btn--sm btn--ghost" onClick={() => { setOffset(0); fetchCalls(0); }}>
          Refresh
        </button>
      </div>

      {error && <p className="call-history__error">{error}</p>}

      {calls.length === 0 && !loading && (
        <p className="call-history__empty">No calls yet</p>
      )}

      <ul className="call-history__list">
        {calls.map((call) => {
          const isExpanded = expanded === call.id;
          const isMine     = call.caller_id === userId;
          return (
            <li key={call.id} className="call-history__item">
              <button
                className="call-history__row"
                onClick={() => setExpanded(isExpanded ? null : call.id)}
              >
                <span className="call-history__direction">{isMine ? '↗' : '↙'}</span>
                <span className="call-history__peer">
                  {isMine ? call.callee_name : call.caller_name}
                </span>
                <span className="call-history__status">{STATUS_LABEL[call.status]}</span>
                <span className="call-history__duration">{formatDuration(call.duration_secs)}</span>
                <time className="call-history__date" dateTime={call.started_at}>
                  {new Date(call.started_at).toLocaleDateString()} {' '}
                  {new Date(call.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </time>
                {call.has_video && <span className="call-history__video-badge">📹</span>}
              </button>

              {isExpanded && call.transcript && (
                <div className="call-history__transcript">
                  <h4>Transcript</h4>
                  <p>{call.transcript}</p>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {loading && <p className="call-history__loading">Loading…</p>}

      {hasMore && !loading && calls.length > 0 && (
        <button className="btn btn--ghost call-history__load-more" onClick={loadMore}>
          Load more
        </button>
      )}
    </div>
  );
};
